import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import t from "../../../../i18n/i18n";
import { useInitUserForm, profileCompletedPercent } from "../utilsUserForm";
import useApi from "../../../../services/api";
import { handleApiErrors } from "../../../../utils/handleErrors";
import { UpdateUser } from "../../../../store/reducers/auth.reducer";

import Input from "../../../../components/form/input";
import Notify from "../../../../components/notification/notify";

import "./styles.css";
import Button from "../../../../components/Button";

function UserForm() {
  useInitUserForm();

  const { ApiUpdateUser, ApiGenerateTFA, ApiSendNotificationPhone, ApiRequestNewEmail } = useApi();

  const [linkToChangeEmailSended, setLinkToChangeEmailSended] = useState(false);

  const dispatch = useDispatch();

  const { user } = useSelector((store) => store.auth);
  const { avatar, email, name, nickname, phone, tfa } = user;

  const [form, setForm] = useState({
    name,
    nickname,
    email,
    phone,
    tfa,
  });

  const [changed, setChanged] = useState({ email: null, phone: null });

  function handleInputChange(e) {
    const targetName = e.target.name;
    const value = e.target.value;

    if (targetName === "name") {
      const validUsername = /^[\w]*$/.test(value);
      if (!validUsername) return;
    }

    setForm({ ...form, [targetName]: value });
  }

  async function tfaSelect() {
    const tfa = form.tfa ? false : true;

    setForm({ ...form, tfa, verifyTFA: tfa });

    if (tfa) {
      await ApiGenerateTFA({ email: form.email, phone: form.phone });
    }
  }

  async function SubmitForm(e) {
    e.preventDefault();

    if (!form.nickname) return Notify({ type: "danger", message: t("Errors.EmptyNickname") });
    if (!form.name) return Notify({ type: "danger", message: t("Errors.EmptyName") });
    if (!form.email) return Notify({ type: "danger", message: t("Errors.EmptyEmail") });

    if (form.verifyTFA && !form.tfa_code) return Notify({ type: "danger", message: t("Errors.EmptyTFA") });

    const { data } = await ApiUpdateUser(form);

    if (data.errors) {
      return handleApiErrors(data.errors);
    }

    dispatch(UpdateUser(data));

    return Notify({ type: "success", message: t("Success.UserUpdated") });
  }

  useEffect(() => {
    const input = document.getElementById("phone");
    input.addEventListener("countrychange", () => {
      const iti = window.intlTelInputGlobals.getInstance(input);

      const { dialCode } = iti.getSelectedCountryData();

      setForm({ ...form, phone: `+${dialCode}` });
    });
  }, [form]);

  async function changeAvatar(e) {
    const reader = new FileReader();

    reader.readAsDataURL(e.target.files[0]);

    reader.onload = async (event) => {
      const { data } = await ApiUpdateUser({ avatar: event.target.result });
      dispatch(UpdateUser(data));
    };
  }

  function randomNumber() {
    return `${Math.ceil(Math.random() * 9)}${Math.ceil(Math.random() * 9)}${Math.ceil(Math.random() * 9)}${Math.ceil(
      Math.random() * 9,
    )}`;
  }

  async function sendLinkToChangeEmail() {
    setLinkToChangeEmailSended(true);

    Notify({ type: "success", message: t("Success.ChangeEmailSended", { email }) });

    await ApiRequestNewEmail();
  }

  async function verificationNeeds(e) {
    if (e.target.name === "phone" && e.target.value !== phone) {
      const code = randomNumber();
      setChanged({ ...changed, phone: { code } });

      if (form.tfa) {
        await ApiSendNotificationPhone({ code, phone: e.target.value });
      }
    }
  }

  function verifyCode(e) {
    if (e.target.name === "email_code") {
      if (e.target.value.length === 4) {
        if (e.target.value !== changed.email.code) {
          return Notify({ type: "danger", message: t("Errors.InvalidEmailCode") });
        } else {
          Notify({ type: "success", message: t("Success.ValidEmailCode") });
          setChanged({ ...changed, email: null });
        }
      }
    }

    if (e.target.name === "phone_code") {
      if (e.target.value.length === 4) {
        if (e.target.value !== changed.phone.code) {
          return Notify({ type: "danger", message: t("Errors.InvalidPhoneCode") });
        } else {
          Notify({ type: "success", message: t("Success.ValidPhoneCode") });
          setChanged({ ...changed, phone: null });
        }
      }
    }
  }

  return (
    <div className="form-col profile-form">
      <div className="user-media">
        <div className="thumb-edit">
          <button
            type="button"
            onClick={() => {
              document.getElementById("avatarInput").click();
            }}
            style={{ border: "none", background: "none" }}
          >
            <img src={avatar || "/images/thumb-large.png"} className="avatar-profile" alt="" />
          </button>

          <input style={{ display: "none" }} id="avatarInput" type="file" onChange={changeAvatar} accept="image/jpeg" />
        </div>
        <div className="user-name">
          {nickname && <h3>{nickname}</h3>}
          <p>@{name}</p>
        </div>
      </div>
      <div className="profile-progress">
        <div className="progress">
          <div
            className="progress-bar"
            role="progressbar"
            style={{ width: `${profileCompletedPercent(user)}%` }}
            aria-valuenow={profileCompletedPercent(user)}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            {t("Profile.Completed", { percent: profileCompletedPercent(user) })}
          </div>
        </div>
      </div>
      <form onSubmit={SubmitForm}>
        <Input
          label={t("Profile.Form.Nickname")}
          name="nickname"
          value={form.nickname || ""}
          onChange={handleInputChange}
        />
        <Input label={t("Profile.Form.Name")} name="name" value={form.name || ""} onChange={handleInputChange} />

        <div className="input-box">
          <label>{t("Profile.Form.Email")}</label>
          <div className="input-change-email-container">
            <input disabled type="text" name="email" value={form.email || ""} />
            <Button disabled={linkToChangeEmailSended} type="button" onClick={sendLinkToChangeEmail}>
              {t("Profile.ChangeEmailButton")}
            </Button>
          </div>
        </div>

        {changed.email && <Input label={t("Profile.Form.VerifyEmailCode")} name="email_code" onChange={verifyCode} />}

        <Input
          label={t("Profile.Form.Phone")}
          type="tel"
          id="phone"
          name="phone"
          value={form.phone || ""}
          onChange={handleInputChange}
          onBlur={verificationNeeds}
        />
        {changed.phone && <Input label={t("Profile.Form.VerifyPhoneCode")} name="phone_code" onChange={verifyCode} />}

        <br />

        <div className="input-box input-check">
          <input type="checkbox" id="tfa" checked={form.tfa} onClick={() => tfaSelect()} />
          <label
            for="tfa"
            dangerouslySetInnerHTML={{
              __html: t("Profile.TFA"),
            }}
          />
        </div>

        {form.verifyTFA && (
          <Input label={t("TFA.Label")} name="tfa_code" value={form.tfa_code || ""} onChange={handleInputChange} />
        )}

        <div className="submit-btn no-border">
          <button type="submit" className="btn-regester" disabled={changed.email || changed.phone}>
            {t("Profile.Form.Submit")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default UserForm;
